import { Injectable } from '@angular/core';
import {
    CombinedMissionStatus,
    FEATURE_LOTAM,
    FEATURE_MISSION_CALENDAR,
    FEATURE_MISSION_REPORTS,
    FEATURE_ORGANISATION_RELATIONSHIPS,
    FEATURE_RPA_LOG_COLLECTION,
    GqlFilterField,
    GqlMissionCriteria,
    hasAnyPermission,
    hasFeatureFlag,
    MissionDto,
    MissionRoleDto,
    MissionSortFieldName,
    PersonDto,
    PersonsOrganisationDto,
    revertCombinedStatus,
    SimplePersonDto
} from '@flyfreely-portal-ui/flyfreely';
import {
    SearchValue,
    TableColumn,
    TableSearchConverter,
    TableSetupUserPreferences,
    TableSortConverter,
    toSearchEntryStateUpdate
} from '@flyfreely-portal-ui/flyfreely-table';
import {
    ConformanceStatusGroup,
    ConformanceStatusGroups,
    FormatPersonPipe,
    toConformanceResultStatusArray
} from '@flyfreely-portal-ui/ui';
import moment from 'moment';
import { BehaviorSubject, Subject } from 'rxjs';

const MISSION_SEARCH_CONVERTER: TableSearchConverter<GqlMissionCriteria> = {
    name: {
        fieldName: 'name',
        valueType: 'string'
    },
    uid: {
        fieldName: 'uid',
        valueType: 'string'
    },
    project: {
        fieldName: 'projectId',
        valueType: 'number'
    },
    locationName: {
        fieldName: 'locationName',
        valueType: 'string'
    },
    'missionApproval.requestTime': {
        fieldName: 'missionApprovalRequestTime',
        valueType: 'timerange'
    },
    'missionApproval.resolutionTime': {
        fieldName: 'missionApprovalResolutionTime',
        valueType: 'timerange'
    },
    type: {
        fieldName: 'missionType',
        valueType: 'string'
    },
    'missionWorkflowVersion.name': {
        fieldName: 'workflowName',
        valueType: 'string'
    },
    'missionType.name': {
        fieldName: 'operationTypeId',
        valueType: 'number'
    },
    craftNicknames: {
        fieldName: 'rpaName',
        valueType: 'string'
    },
    rpic: {
        fieldName: 'rpicName',
        valueType: 'string'
    },
    missionCrewNames: {
        fieldName: 'crewName',
        valueType: 'string'
    }
};

const MISSION_SORT_CONVERTER: TableSortConverter<MissionSortFieldName> = {
    name: 'name',
    'missionWorkflowVersion.name': 'workflowName',
    locationName: 'locationName',
    missionDate: 'missionDate',
    rpic: 'rpic'
};

/**
 * This class provides data manipulation support to the mission view component,
 * as well as some state services, such as the view mode. It also acts as a communication bus between
 * components in the mission-view package.
 */
@Injectable()
export class MissionViewService {
    private viewModeSource = new BehaviorSubject<'MAP' | 'LIST' | 'BOTH'>(
        'BOTH'
    );
    viewMode$ = this.viewModeSource.asObservable();

    currentOrganisation: PersonsOrganisationDto;

    private showReportsDialogueSource = new Subject<void>();
    private showMissionCalendarSource = new Subject<void>();
    private showOrganisationLotamsSource = new Subject<void>();
    private permissionsUpdatedSource = new BehaviorSubject<boolean>(false);
    private isMissionDataLoading = new BehaviorSubject<boolean>(false);
    showReportsDialogue$ = this.showReportsDialogueSource.asObservable();
    showMissionCalendar$ = this.showMissionCalendarSource.asObservable();
    showOrganisationLotams$ = this.showOrganisationLotamsSource.asObservable();
    permissionsUpdated$ = this.permissionsUpdatedSource.asObservable();
    isMissionDataLoading$ = this.isMissionDataLoading.asObservable();

    canFilterByAuthority = false;
    canUseCalendar = true;
    canUseLotams = false;
    canListMissions = true;
    canSeeOwnMissions = true;
    hasFlightLogging = true;
    canReport = true;
    canAddMission = true;
    canEditMission = true;
    canViewOrganisation = false;

    constructor(private formatPersonPipe: FormatPersonPipe) {}

    ngOnDestroy() {
        this.viewModeSource.complete();
        this.showReportsDialogueSource.complete();
        this.showMissionCalendarSource.complete();
        this.showOrganisationLotamsSource.complete();
        this.permissionsUpdatedSource.complete();
        this.isMissionDataLoading.complete();
    }

    setViewMode(viewMode: 'MAP' | 'LIST' | 'BOTH') {
        this.viewModeSource.next(viewMode);
    }

    refreshPermissions(organisation: PersonsOrganisationDto) {
        this.currentOrganisation = organisation;
        if (this.currentOrganisation == null) {
            this.canAddMission = false;
            this.canEditMission = false;
            this.canListMissions = false;
            this.canSeeOwnMissions = false;
            this.canFilterByAuthority = false;
            this.canUseLotams = false;
            this.canViewOrganisation = false;
            return;
        }

        this.canAddMission = hasAnyPermission(this.currentOrganisation, [
            PersonsOrganisationDto.Permissions.MISSION_ADD,
            PersonsOrganisationDto.Permissions.MISSION_ADD_OWN
        ]);

        this.canEditMission = hasAnyPermission(
            this.currentOrganisation,
            PersonsOrganisationDto.Permissions.MISSION_EDIT
        );

        this.canListMissions = hasAnyPermission(this.currentOrganisation, [
            PersonsOrganisationDto.Permissions.MISSION_LIST
        ]);

        this.canSeeOwnMissions = hasAnyPermission(this.currentOrganisation, [
            PersonsOrganisationDto.Permissions.MISSION_VIEW
        ]);

        this.canViewOrganisation = hasAnyPermission(this.currentOrganisation, [
            PersonsOrganisationDto.Permissions.ORGANISATION_VIEW
        ]);

        this.canFilterByAuthority = hasFeatureFlag(
            this.currentOrganisation,
            FEATURE_ORGANISATION_RELATIONSHIPS
        );

        this.hasFlightLogging =
            hasFeatureFlag(
                this.currentOrganisation,
                FEATURE_RPA_LOG_COLLECTION
            ) &&
            hasAnyPermission(this.currentOrganisation, [
                PersonsOrganisationDto.Permissions.FLIGHT_LOG_COLLECTION_MANAGE,
                PersonsOrganisationDto.Permissions.FLIGHT_LOG_COLLECTION_OWN,
                PersonsOrganisationDto.Permissions.FLIGHT_LOG_MANAGE
            ]);

        this.canReport = hasFeatureFlag(
            this.currentOrganisation,
            FEATURE_MISSION_REPORTS
        );

        this.canUseCalendar = hasFeatureFlag(
            this.currentOrganisation,
            FEATURE_MISSION_CALENDAR
        );

        this.canUseLotams = hasFeatureFlag(
            this.currentOrganisation,
            FEATURE_LOTAM
        );

        this.permissionsUpdatedSource.next(true);
    }

    findCrew(
        missionCrew: {
            person?: SimplePersonDto | PersonDto;
            role?: { coreRole?: MissionRoleDto.CoreRole };
        }[]
    ) {
        return missionCrew
            .filter(c => c.role.coreRole !== 'PILOT_IN_COMMAND')
            .map(c => this.formatPersonPipe.transform(c.person));
    }

    convertSortFieldName(field: string | null): MissionSortFieldName | null {
        if (field == null) {
            return null;
        }
        if (field in MISSION_SORT_CONVERTER) {
            return MISSION_SORT_CONVERTER[field];
        }

        return null;
    }

    /**
     * Convert to a state update, meaning, set to null if the search doesn't apply.
     *
     * @param field table field name
     * @param value search value
     * @returns the entries for the GqlMissionCriteria object state update
     */
    toSearchEntryStateUpdate(
        field: string,
        value: SearchValue
    ): {
        [P in keyof GqlMissionCriteria]?: GqlMissionCriteria[P] | null;
    } {
        if (field === 'missionDate') {
            if (Array.isArray(value) && value.length == 2) {
                return {
                    startTime: value[0],
                    endTime: value[1]
                };
            }
            return {
                startTime: null,
                endTime: null
            };
        }

        if (field === 'combinedStatus') {
            // not even the half of it
            const deconstructedStatus = revertCombinedStatus(
                value as CombinedMissionStatus | null
            );

            return {
                readyForFinalisation: deconstructedStatus.readyForFinalisation,
                status: deconstructedStatus.missionStatus,
                missionApprovalStatus: deconstructedStatus.approvalStatus,
                outstandingApprovals: deconstructedStatus.outstandingApprovals
            };
        }

        if (field === 'flightConformanceResultList') {
            if (
                ConformanceStatusGroups.includes(
                    value as ConformanceStatusGroup
                )
            ) {
                return {
                    conformanceResultStatus: toConformanceResultStatusArray(
                        value as ConformanceStatusGroup
                    )
                };
            }
            return { conformanceResultStatus: null };
        }

        return toSearchEntryStateUpdate(field, value, MISSION_SEARCH_CONVERTER);
    }

    private createCombinedDeconstructedStatuses(
        combinedStatus: CombinedMissionStatus | null
    ) {
        const deconstructedStatus = revertCombinedStatus(combinedStatus);
        const combinedDeconstructed = { combinedStatus, deconstructedStatus };
        return combinedDeconstructed;
    }

    updateMissionStatusFilters(search: Record<string, unknown>) {
        const searchCombinedStatus =
            (search.combinedStatus as CombinedMissionStatus) ?? null;

        const { combinedStatus, deconstructedStatus } =
            this.createCombinedDeconstructedStatuses(searchCombinedStatus);
        return deconstructedStatus?.missionStatus;
    }

    filterCombinedStatus(search: Record<string, unknown>) {
        const searchCombinedStatus =
            (search.combinedStatus as CombinedMissionStatus) ?? null;

        const { combinedStatus, deconstructedStatus } =
            this.createCombinedDeconstructedStatuses(searchCombinedStatus);
        if (combinedStatus == null || deconstructedStatus == null) {
            return [];
        }
        const approvalStatuses: GqlFilterField[] =
            deconstructedStatus.approvalStatus == null ||
            deconstructedStatus.approvalStatus.length === 0
                ? []
                : deconstructedStatus.approvalStatus.map(s => ({
                      field: 'status',
                      filter: s.toString()
                  }));
        const readyForFinalisation: GqlFilterField[] =
            deconstructedStatus.readyForFinalisation == null
                ? []
                : [
                      {
                          field: 'readyForFinalisation',
                          filter: deconstructedStatus.readyForFinalisation
                              ? 'true'
                              : 'false'
                      }
                  ];
        const outstandingApprovals: GqlFilterField[] =
            deconstructedStatus.outstandingApprovals == null
                ? []
                : [
                      {
                          field: 'outstandingApprovals',
                          filter: deconstructedStatus.outstandingApprovals
                              ? 'true'
                              : 'false'
                      }
                  ];
        return approvalStatuses
            .concat(readyForFinalisation)
            .concat(outstandingApprovals);
    }

    fixFilter(availableColumns: TableColumn[]) {
        return (p: TableSetupUserPreferences) => {
            const sort =
                availableColumns != null &&
                p?.columnSorting?.column != null &&
                (availableColumns.find(
                    c =>
                        c.value === p.columnSorting.column ||
                        c.key === p.columnSorting.column
                ) == null ||
                    availableColumns.find(
                        c =>
                            c.value === p.columnSorting.column ||
                            c.key === p.columnSorting.column
                    ).sortable === false)
                    ? {
                          ...p,
                          columnSorting: null
                      }
                    : p;

            const pref =
                sort?.tableSearch?.combinedStatuses ===
                'DRAFT|READY_TO_FLY|PREPARED|FLYING|ON_SITE|DONE_FLYING|COMPLETED'
                    ? {
                          ...sort,
                          tableSearch: {
                              ...sort.tableSearch,
                              combinedStatuses:
                                  'DRAFT|SUBMITTED|READY_TO_FLY|PREPARED|FLYING|ON_SITE|DONE_FLYING|COMPLETED|READY_FOR_FINALISATION'
                          }
                      }
                    : sort;
            if (pref?.tableSearch != null) {
                const getDates = (k: string) => {
                    return pref.tableSearch[k] != null
                        ? (<string[]>pref.tableSearch[k]).map(d =>
                              moment(d).toDate()
                          )
                        : null;
                };
                const search = Object.keys(pref.tableSearch).reduce(
                    (acc, k) => {
                        if (
                            k.toString() ===
                            'missionWorkflowVersion.workflowName'
                        ) {
                            return {
                                ...acc,
                                ['missionWorkflowVersion.name']:
                                    pref.tableSearch[
                                        'missionWorkflowVersion.workflowName'
                                    ]
                            };
                        } else if (
                            k.toString() === 'missionDate' ||
                            k.toString() === 'missionApproval.requestTime' ||
                            k.toString() === 'missionApproval.resolutionTime'
                        ) {
                            if (
                                pref.tableSearch[k] == null ||
                                (<string[]>pref.tableSearch[k]).filter(
                                    s => s != null
                                ).length === 0
                            ) {
                                return {
                                    ...acc,
                                    [k]: null
                                };
                            }
                            return {
                                ...acc,
                                [k]: getDates(k.toString())
                            };
                        } else if (k === 'type') {
                            return {
                                ...acc,
                                type: this.validateOptions(
                                    <string>pref.tableSearch[k],
                                    [
                                        MissionDto.Type.STANDARD,
                                        MissionDto.Type.RETROSPECTIVE,
                                        MissionDto.Type.OFFLINE,
                                        MissionDto.Type.FIELD_APP_BACKUP,
                                        MissionDto.Type.IMPORTED
                                    ]
                                )
                            };
                        } else {
                            return {
                                ...acc,
                                [k]: pref.tableSearch[k]
                            };
                        }
                    },
                    {}
                );
                return {
                    ...pref,
                    tableSearch: {
                        ...search
                    }
                };
            } else {
                return pref;
            }
        };
    }

    private validateOptions(current: string, options: any[]): string {
        if (current == null) {
            return current;
        }

        for (const option of options) {
            if (option === current) {
                return current;
            }
        }

        return null;
    }

    showMissionReportsDialogue() {
        this.showReportsDialogueSource.next();
    }

    showMissionCalendar() {
        this.showMissionCalendarSource.next();
    }

    showOrganisationLotams() {
        this.showOrganisationLotamsSource.next();
    }

    setMissionDataLoading(isLoading: boolean): void {
        this.isMissionDataLoading.next(isLoading);
    }
}
